<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="flex: 1 1 220px; max-width: 320px;">
              <label class="filter-label">Buscar</label>
              <b-form-input v-model="filters.search" size="sm" placeholder="Código o cliente" @keyup.enter="filterCustomers" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 200px; max-width: 280px;">
              <label class="filter-label">Zona</label>
              <v-select v-model="filters.zone" :options="arr.zones" label="name" placeholder="Todas" :clearable="true" :select-on-tab="true" />
            </div>
            <b-button variant="outline-dark" size="sm" v-b-tooltip.hover title="Aplicar filtros" aria-label="Aplicar filtros"
                      style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;" @click="filterCustomers">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-card>
        <b-table v-if="table.items.length || table.isBusy"
                 class="app-table seller-customers-table"
                 responsive
                 :stacked="isMobileViewport ? 'md' : false"
                 sticky-header="60vh"
                 hover
                 small
                 :items="table.items"
                 :fields="table.fields"
                 :current-page="table.currentPage"
                 :per-page="table.perPage"
                 :busy="table.isBusy"
                 @row-clicked="onRowClicked">
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle" />
              <strong>Cargando...</strong>
            </div>
          </template>

          <template #cell(code)="data"><strong>{{ data.item.code }}</strong></template>

          <template #cell(customers_id)="data">
            <div class="d-flex align-items-center">
              <b-avatar v-if="data.item.image" :src="data.item.image" size="25px" />
              <b-avatar v-else
                        size="25px"
                        :text="avatarFromName(data.item.name).text"
                        :style="avatarFromName(data.item.name).style" />
              <strong class="ml-2">{{ data.item.name }}</strong>
              <AppIcon v-if="!data.item.active"
                       name="circle-fill"
                       fill
                       class="ml-2 text-danger"
                       v-b-tooltip.hover
                       title="Cliente inactivo" />
            </div>
          </template>
          <template #cell(zone)="data">{{ data.item.seller_zone ? data.item.seller_zone.name : 'Sin zona' }}</template>

          <template #cell(last_news)="data">{{ reportDateLabel(data.item, 'news') }}</template>
          <template #cell(last_payment)="data">{{ reportDateLabel(data.item, 'payments') }}</template>
          <template #cell(last_visits)="data">{{ reportDateLabel(data.item, 'visits') }}</template>
          <template #cell(last_services)="data">{{ reportDateLabel(data.item, 'services') }}</template>
          <template #cell(last_marketing)="data">{{ reportDateLabel(data.item, 'marketing') }}</template>
          <template #cell(last_audit)="data">{{ reportDateLabel(data.item, 'audit') }}</template>
          <template #cell(last_order)="data">{{ orderDateLabel(data.item) }}</template>

          <template #cell(f_action)="data">
            <b-dropdown right
                        no-caret
                        size="sm"
                        variant="link"
                        boundary="window"
                        :toggle-class="isMobileViewport ? 'seller-customers-action-toggle-labeled' : 'app-table-action-toggle'"
                        :class="{ 'pull-right': !isMobileViewport }">
              <template #button-content>
                <AppIcon name="more_vert" :label="isMobileViewport ? 'Opciones' : 'Acciones'" :class="{ 'mr-1': isMobileViewport }" />
                <span v-if="isMobileViewport">Opciones</span>
              </template>
              <b-dropdown-item @click="goDetailCustomer(data.item)"><AppIcon name="visibility" /> Detalle cliente</b-dropdown-item>
              <b-dropdown-header>Pedidos</b-dropdown-header>
              <b-dropdown-item @click="addOrder(data.item)"><AppIcon name="add_shopping_cart" /> Agregar pedido</b-dropdown-item>
              <b-dropdown-header>Reportes</b-dropdown-header>
              <b-dropdown-item v-if="accessExtras.INFORME_VISITAS.status" @click="goReportVisit(data.item)"><AppIcon name="location_on" /> Reporte de visitas</b-dropdown-item>
              <b-dropdown-item v-if="accessExtras.INFORME_COBRANZAS.status" @click="goReportPayment(data.item)"><AppIcon name="payments" /> Reporte de cobranzas</b-dropdown-item>
              <b-dropdown-item v-if="accessExtras.INFORME_NOVEDADES.status" @click="goReportNews(data.item)"><AppIcon name="chat" /> Reporte de novedades</b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>

        <EmptyState v-else icon="business" title="No se encontraron clientes" description="No hay clientes asignados para mostrar." />

        <div class="app-table-footer">
          <span class="list-view-toolbar__count">{{ table.items.length }} registros</span>
        </div>
      </b-card>

      <b-card body-class="py-2">
        <section class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="arrow_back" class="mr-1" /> Volver
          </b-button>
          <b-pagination class="mb-0"
                        size="sm"
                        pills
                        :total-rows="table.items.length"
                        :per-page="table.perPage"
                        v-model="table.currentPage" />
        </section>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import serviceAPI from './services'
import Error from '@/handler/error'
import Profiles from '@/config/profiles'
import Modules from '@/config/modules'
import Helper from '@/handler/helper'
import Funciones from '@/handler/funciones'
import StorageLocal from '@/handler/storageLocal'
import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
import moment from 'moment'

export default {
  components: { TabHeaderActions },
  data: () => ({
    access: {
      module_id: Modules.VENDEDORES,
      profile_id: Profiles.PERSONAL,
      view_reference: 'listCustomersSellers',
      elements: {},
    },
    accessExtras: {
      INFORME_VISITAS: { module: Modules.INFORME_VISITAS, status: false },
      INFORME_COBRANZAS: { module: Modules.INFORME_COBRANZAS, status: false },
      INFORME_NOVEDADES: { module: Modules.INFORME_NOVEDADES, status: false },
      INFORME_SERVICIOS: { module: Modules.INFORME_SERVICIOS, status: false },
      INFORME_MARKETING: { module: Modules.INFORME_MARKETING, status: false },
      INFORME_AUDITORIA: { module: Modules.INFORME_AUDITORIA, status: false },
    },
    filtersOpen: false,
    isMobileViewport: false,
    mobileMql: null,
    table: {
      items: [],
      fields: [],
      currentPage: 1,
      perPage: 50,
      isBusy: false,
    },
    filters: {
      search: '',
      zone: null,
    },
    arr: {
      zones: [],
    },
  }),
  watch: {
    filtersOpen(value) {
      StorageLocal.setValue('filters_open_list_customers_sellers', value ? 'true' : 'false')
    },
    isMobileViewport() {
      this.loadFieldTable()
    },
  },
  created() {
    Helper.hasAccessView(this.access)
    Object.keys(this.accessExtras).forEach(key => {
      this.accessExtras[key].status = Helper.moduleEnabled(this.accessExtras[key].module)
    })
  },
  mounted() {
    this.filtersOpen = StorageLocal.getValue('filters_open_list_customers_sellers') === 'true'
    this.mobileMql = window.matchMedia('(max-width: 767.98px)')
    this.isMobileViewport = this.mobileMql.matches
    this.mobileMql.addEventListener('change', this.updateMobileViewport)
    this.loadFieldTable()
    this.loadZones()
    this.filterCustomers()
  },
  beforeDestroy() {
    if (this.mobileMql) this.mobileMql.removeEventListener('change', this.updateMobileViewport)
  },
  methods: {
    updateMobileViewport(event) {
      this.isMobileViewport = event.matches
    },
    alignClass(desktopClass) {
      return this.isMobileViewport ? 'text-left' : desktopClass
    },
    avatarFromName(name) {
      return Funciones.avatarFromName(name)
    },
    loadFieldTable() {
      this.table.fields = [
        { key: 'code', label: 'Código', class: 'align-middle ' + this.alignClass('text-center'), width: '90px' },
        { key: 'customers_id', label: 'Cliente', class: 'align-middle ' + this.alignClass('text-left') },
        { key: 'zone', label: 'Zona', class: 'align-middle ' + this.alignClass('text-left') },
      ]
      if (this.accessExtras.INFORME_VISITAS.status) this.table.fields.push({ key: 'last_visits', label: 'Última visita', class: 'align-middle ' + this.alignClass('text-center') })
      if (this.accessExtras.INFORME_COBRANZAS.status) this.table.fields.push({ key: 'last_payment', label: 'Última cobranza', class: 'align-middle ' + this.alignClass('text-center') })
      if (this.accessExtras.INFORME_NOVEDADES.status) this.table.fields.push({ key: 'last_news', label: 'Última novedad', class: 'align-middle ' + this.alignClass('text-center') })
      if (this.accessExtras.INFORME_SERVICIOS.status) this.table.fields.push({ key: 'last_services', label: 'Último servicio', class: 'align-middle ' + this.alignClass('text-center') })
      if (this.accessExtras.INFORME_MARKETING.status) this.table.fields.push({ key: 'last_marketing', label: 'Último marketing', class: 'align-middle ' + this.alignClass('text-center') })
      if (this.accessExtras.INFORME_AUDITORIA.status) this.table.fields.push({ key: 'last_audit', label: 'Última auditoría', class: 'align-middle ' + this.alignClass('text-center') })
      this.table.fields.push({ key: 'last_order', label: 'Último pedido', class: 'align-middle ' + this.alignClass('text-center') })
      this.table.fields.push({ key: 'f_action', label: this.isMobileViewport ? 'Acción' : '', class: 'align-middle ' + this.alignClass('text-right'), width: '50px' })
    },
    loadZones() {
      serviceAPI.obtenerZonasByVendedor({ sellers_id: Helper.getSeller().id })
        .then((response) => { this.arr.zones = response.data || [] })
        .catch((error) => { this.$awn.alert(Error.showError(error)) })
    },
    filterCustomers() {
      this.table.isBusy = true
      serviceAPI.obtenerClientesByZonas({
        sellers_id: Helper.getSeller().id,
        getLastReports: true,
        search: this.filters.search,
        zone_id: this.filters.zone ? this.filters.zone.id : null,
      })
        .then((response) => {
          this.table.items = response.data || []
          this.table.isBusy = false
        })
        .catch((error) => {
          this.table.isBusy = false
          this.$awn.alert(Error.showError(error))
        })
    },
    reportDateLabel(item, type) {
      var report = item['last-reports-' + type]
      return report ? moment.duration(moment().diff(moment(report.date), 'days'), 'days').humanize() : 'Sin cargar'
    },
    orderDateLabel(item) {
      return item['last-order'] ? moment(item['last-order'].date).format('DD/MM/YYYY') : 'Sin pedidos'
    },
    onRowClicked(item, index, event) {
      if (event && event.target && event.target.closest('button, a, input, select, textarea, .dropdown')) return
      this.goDetailCustomer(item)
    },
    goReportVisit(item) {
      this.$router.push({ name: 'ReportsVisitsStaffCrudReportSellerByCustomers', params: { customersID: item.id } })
    },
    goReportPayment(item) {
      this.$router.push({ name: 'ReportsPaymentStaffCrudReportSellerByCustomers', params: { customersID: item.id + '|' + item.code } })
    },
    goReportNews(item) {
      this.$router.push({ name: 'ReportsNewsStaffCrudReportSellerByCustomers', params: { customersID: item.id } })
    },
    addOrder(item) {
      this.$router.push({ name: 'OrdersStaffCrudOrdersSellers', query: { customer_id: item.id } })
    },
    goDetailCustomer(item) {
      this.$router.push({ name: 'CustomerViewStaff', params: { customersID: item.id } })
    },
  },
}
</script>

<style>
.btn.seller-customers-action-toggle-labeled {
  display: inline-flex !important;
  align-items: center;
  justify-content: flex-start;
  width: auto !important;
  height: auto !important;
  min-height: 0;
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  color: var(--app-color-text-secondary) !important;
  font-size: 13px;
  line-height: 1.15;
}
.btn.seller-customers-action-toggle-labeled:hover,
.btn.seller-customers-action-toggle-labeled:focus {
  color: var(--app-color-primary) !important;
  text-decoration: underline;
}
@media (max-width: 767.98px) {
  .seller-customers-table.app-table.b-table-stacked-md > tbody > tr > td:last-child::before {
    content: attr(data-label) !important;
  }
  .seller-customers-table.app-table.b-table-stacked-md > tbody > tr > td:last-child {
    text-align: left !important;
  }
}
</style>
